import { inflateRaw } from './deflate';
import {
  DOC_PACK_HEADER_BYTES,
  DOC_PACK_MAGIC,
  type DocPackCatalog,
  DocPackError,
  type DocPackPage,
  type DocPackPageCharRange,
} from './types';
import { Predicate, Schema } from 'effect';

const textDecoder = new TextDecoder();

/**
 * Hard ceiling on the header's declared catalog length. Real catalogs run a few
 * hundred bytes per page (a 5,000-page doc is ~2MB), so 64MB is orders of
 * magnitude of headroom. The cap exists because `catalogByteLength` is an
 * unvalidated LE uint32: a header whose magic survived but whose length bytes
 * are corrupt could claim ~4GB and make a downstream `new Uint8Array(...)`
 * (see openDocPackCatalogFromUrl) OOM-crash before any bounds check runs. A
 * length past the cap is corruption, not a real pack.
 */
export const DOC_PACK_MAX_CATALOG_BYTES = 64 * 1024 * 1024;

export function readDocPackHeader(bytes: Uint8Array): {
  catalogByteLength: number;
  blobBaseOffset: number;
} {
  if (bytes.length < DOC_PACK_HEADER_BYTES) {
    throw DocPackError.make({
      code: 'truncated',
      message: 'Pack shorter than its header',
    });
  }
  const magic = textDecoder.decode(bytes.subarray(0, 8));
  if (magic !== DOC_PACK_MAGIC) {
    throw DocPackError.make({
      code: 'bad-magic',
      message: `Unexpected magic ${magic}`,
    });
  }
  const catalogByteLength = new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength,
  ).getUint32(8, true);
  if (catalogByteLength > DOC_PACK_MAX_CATALOG_BYTES) {
    throw DocPackError.make({
      code: 'invalid-catalog',
      message: `Catalog length ${catalogByteLength.toString()} exceeds max ${DOC_PACK_MAX_CATALOG_BYTES.toString()} — corrupt header`,
    });
  }
  return {
    catalogByteLength,
    blobBaseOffset: DOC_PACK_HEADER_BYTES + catalogByteLength,
  };
}

const isCatalogShape = Schema.is(
  Schema.Struct({
    v: Schema.Literal(1),
    audioId: Schema.String,
    contentVersion: Schema.Finite,
    pageCount: Schema.Finite,
    pages: Schema.Array(
      Schema.Struct({
        n: Schema.Finite,
        offset: Schema.Finite,
        length: Schema.Finite,
      }),
    ),
    outline: Schema.Array(Schema.Unknown),
  }),
);

const isPageShape = Schema.is(
  Schema.Struct({
    page: Schema.Finite,
    text: Schema.String,
    words: Schema.Array(Schema.Unknown),
    sentences: Schema.Array(Schema.Unknown),
    blocks: Schema.Array(Schema.Unknown),
    segments: Schema.Array(Schema.Unknown),
  }),
);

/** The shape checks the reader trusts a decoded catalog and page by. */
export const isDocPackCatalog = (u: unknown): u is DocPackCatalog =>
  isCatalogShape(u);
export const isDocPackPage = (u: unknown): u is DocPackPage => isPageShape(u);

const validateCatalog = (parsed: unknown) => {
  if (!isCatalogShape(parsed)) {
    throw DocPackError.make({
      code: 'invalid-catalog',
      message: 'Catalog failed validation',
    });
  }
  return parsed as DocPackCatalog;
};

export async function readDocPackCatalog(prefix: Uint8Array): Promise<{
  catalog: DocPackCatalog;
  blobBaseOffset: number;
}> {
  const { catalogByteLength, blobBaseOffset } = readDocPackHeader(prefix);
  if (prefix.length < DOC_PACK_HEADER_BYTES + catalogByteLength) {
    throw DocPackError.make({
      code: 'truncated',
      message: `Catalog needs ${(DOC_PACK_HEADER_BYTES + catalogByteLength).toString()} bytes, got ${prefix.length.toString()}`,
    });
  }
  const inflated = await inflateRaw(
    prefix.subarray(
      DOC_PACK_HEADER_BYTES,
      DOC_PACK_HEADER_BYTES + catalogByteLength,
    ),
  );
  return {
    catalog: validateCatalog(JSON.parse(textDecoder.decode(inflated))),
    blobBaseOffset,
  };
}

export function docPackPageByteRange(
  catalog: DocPackCatalog,
  blobBaseOffset: number,
  page: number,
): { start: number; endInclusive: number } | null {
  const span = catalog.pages.find((p) => p.n === page);
  if (!span) return null;
  return {
    start: blobBaseOffset + span.offset,
    endInclusive: blobBaseOffset + span.offset + span.length - 1,
  };
}

export async function decodeDocPackPage(
  page: number,
  blobBytes: Uint8Array,
): Promise<DocPackPage> {
  const inflated = await inflateRaw(blobBytes);
  const parsed = JSON.parse(textDecoder.decode(inflated)) as unknown;
  if (!Predicate.isObjectOrArray(parsed)) {
    throw DocPackError.make({
      code: 'invalid-page',
      message: `Page ${page.toString()} is not an object`,
    });
  }
  if (!isPageShape(parsed) || parsed.page !== page) {
    throw DocPackError.make({
      code: 'invalid-page',
      message: `Page ${page.toString()} failed validation`,
    });
  }
  return parsed as DocPackPage;
}

export const shouldIncludeDocPackRangeTextPart = (part: string) =>
  part.trim().length > 0;

export const glueDocPackRangeTexts = (parts: readonly string[]) =>
  parts.filter(shouldIncludeDocPackRangeTextPart).join(' ');

export function resolveDocPackRanges(
  pages: ReadonlyMap<number, DocPackPage>,
  ranges: readonly DocPackPageCharRange[],
) {
  return resolveDocPackRangesWithWords(pages, ranges).text;
}

export type DocPackResolvedWord = {
  readonly page: number;
  readonly charStart: number;
  readonly charEnd: number;
};

export type DocPackResolvedRanges = {
  readonly text: string;
  readonly words: readonly DocPackResolvedWord[];
};

/**
 * Resolve a list of (page, charStart, charEnd) ranges into the spoken text AND
 * per-word char-offset provenance, applying the SAME glue rule as
 * {@link glueDocPackRangeTexts} (blank parts skipped, non-blank parts joined by a
 * single space). This is the single source of truth shared by the client
 * reading plan (chunk assembly) and the server `/v2/synthesize` reference path,
 * so resolved text stays byte-identical and the `hash(text+voice)` cache key
 * never diverges. Word provenance offsets are page-relative into `page.text`.
 */
export function resolveDocPackRangesWithWords(
  pages: ReadonlyMap<number, DocPackPage>,
  ranges: readonly DocPackPageCharRange[],
): DocPackResolvedRanges {
  const parts: string[] = [];
  const words: DocPackResolvedWord[] = [];
  for (const [pageNumber, start, end] of ranges) {
    const page = pages.get(pageNumber);
    if (!page) {
      throw DocPackError.make({
        code: 'invalid-range',
        message: `Missing page ${pageNumber.toString()} for doc-pack range`,
      });
    }
    if (start < 0 || end < start || end > page.text.length) {
      throw DocPackError.make({
        code: 'invalid-range',
        message: `Invalid range ${pageNumber.toString()}:${start.toString()}-${end.toString()}`,
      });
    }
    const slice = page.text.slice(start, end);
    parts.push(slice);
    if (!shouldIncludeDocPackRangeTextPart(slice)) continue;
    for (const match of slice.matchAll(/\S+/g)) {
      words.push({
        page: pageNumber,
        charStart: start + match.index,
        charEnd: start + match.index + match[0].length,
      });
    }
  }
  // Text is produced by the shared glue helper so it is, by construction,
  // identical to what the client assembles for the same ranges.
  return { text: glueDocPackRangeTexts(parts), words };
}
